import type { Level } from '../core/hp/types';
import type { Selection } from '../state/store';
import type { FloorRef, PresenceEvent, SelectionTarget } from './protocol';

export type SelectionHolder = { key: string; userId: string; name: string; color: string };
/** One target and everyone holding it, in claim order; the outline wears the first holder's colour (the app's `SelectionHolders`). */
export type ParticipantSelection = { key: string; userId: string; color: string; target: SelectionTarget; holders: SelectionHolder[] };
const MAX_SELECTION = 256;

export function isPresenceSelection(value: unknown): value is SelectionTarget[] | null | undefined {
  return value == null || Array.isArray(value) && value.length <= MAX_SELECTION && value.every(target => target &&
    (['item', 'wall', 'opening'].includes(target.kind) ? typeof target.id === 'string' && target.id.length > 0 && target.id.length <= 256
      : target.kind === 'room' && Number.isSafeInteger(target.index) && target.index >= 0));
}

export function captureSelection(selection: Selection): SelectionTarget[] {
  if (!selection) return [];
  if (selection.kind === 'items') return [...new Set(selection.ids)].slice(0, MAX_SELECTION).map(id => ({ kind: 'item', id }));
  // A corner, a plinth or a molding is shown to the others as its wall.
  if (selection.kind === 'vertex' || selection.kind === 'trim') return [{ kind: 'wall', id: selection.wallId }];
  return [selection.kind === 'room' ? { kind: 'room', index: selection.index } : { kind: selection.kind, id: selection.id }];
}

export const selectionKey = (target: SelectionTarget) => `${target.kind}:${target.kind === 'room' ? target.index : target.id}`;

const entityKeys = new WeakMap<Level, Set<string>>();

/** Resolve against the presented floor so a removed object cannot leave a stale outline; `people` come in claim order. */
export function participantSelections(people: readonly PresenceEvent[], floor: FloorRef, level: Level,
  color: (userId: string) => string): ParticipantSelection[] {
  let exists = entityKeys.get(level);
  if (!exists) { exists = new Set([...level.items.map(value => `item:${value.id}`), ...level.walls.map(value => `wall:${value.id}`),
    ...level.openings.map(value => `opening:${value.id}`), ...level.rooms.map(value => `room:${value.index}`)]); entityKeys.set(level, exists); }
  const result = new Map<string, ParticipantSelection>();
  for (const person of people) {
    if (person.floor.slot !== floor.slot || person.floor.layoutVersion !== floor.layoutVersion || !isPresenceSelection(person.selection)) continue;
    const seen = new Set<string>();
    // Older clients announce only gestures; outline their changed entities in the same colour.
    const targets = person.selection !== undefined ? person.selection ?? [] : (person.preview ? [
      ...person.preview.delta.items.upsert.map(item => ({ kind: 'item' as const, id: item.id })),
      ...person.preview.delta.walls.upsert.map(wall => ({ kind: 'wall' as const, id: wall.id })),
      ...person.preview.delta.openings.upsert.map(opening => ({ kind: 'opening' as const, id: opening.id })),
    ].slice(0, MAX_SELECTION) : []);
    for (const target of targets) {
      const key = selectionKey(target);
      if (!exists.has(key) || seen.has(key)) continue;
      seen.add(key);
      const holder = { key: `${person.userId}:${person.clientId}`, userId: person.userId, name: person.displayName, color: color(person.userId) };
      const held = result.get(key);
      if (held) held.holders.push(holder);
      else result.set(key, { key, userId: holder.userId, color: holder.color, target, holders: [holder] });
    }
  }
  return [...result.values()];
}

export type ScreenBox = { left: number; top: number; right: number; bottom: number };
export type StackedHolder = { holder: SelectionHolder; index: number; x: number; y: number };
/** Figma's step between the stacked 24 px name pills (the app's StackStepPoints is 20 for its 20 px label) and the gap from the frame. */
export const STACK_STEP = 28, STACK_GAP = 8;

/**
 * The app's `SelectionLabelStack`: one pill per holder beside the union of what they hold, those holding the same things
 * stacked top to bottom in claim order, kept inside `view`. A stack whose frame is out of view is left out, so its holders
 * keep their own markers. `centres`: where each holder's gaze turns, shown or not.
 */
export function holderStacks(selections: readonly ParticipantSelection[], boxOf: (selection: ParticipantSelection) => ScreenBox | null,
  view: ScreenBox, pillWidth: (holderKey: string) => number): { stacks: StackedHolder[]; centres: Map<string, { x: number; y: number }> } {
  const held = new Map<string, { holder: SelectionHolder; box: ScreenBox; targets: string[] }>();
  for (const selection of selections) {
    const box = boxOf(selection);
    if (box) for (const holder of selection.holders) {
      const entry = held.get(holder.key);
      if (!entry) { held.set(holder.key, { holder, box: { ...box }, targets: [selection.key] }); continue; }
      entry.targets.push(selection.key);
      entry.box = { left: Math.min(entry.box.left, box.left), top: Math.min(entry.box.top, box.top), right: Math.max(entry.box.right, box.right), bottom: Math.max(entry.box.bottom, box.bottom) };
    }
  }
  const groups = new Map<string, { box: ScreenBox; holders: SelectionHolder[] }>(), centres = new Map<string, { x: number; y: number }>();
  for (const { holder, box, targets } of held.values()) {
    centres.set(holder.key, { x: (box.left + box.right) / 2, y: (box.top + box.bottom) / 2 });
    const key = targets.join('\u0000');
    (groups.get(key) ?? groups.set(key, { box, holders: [] }).get(key)!).holders.push(holder);
  }
  const stacks: StackedHolder[] = [];
  for (const { box, holders } of groups.values()) {
    if (box.right < view.left || box.bottom < view.top || box.left > view.right || box.top > view.bottom) continue;
    for (const [index, holder] of holders.entries()) {
      const width = pillWidth(holder.key);
      const right = box.right + STACK_GAP, left = box.left - STACK_GAP - width;
      const x = Math.max(view.left, Math.min(right + width > view.right && left >= view.left ? left : right, view.right - width));
      const y = Math.max(view.top, Math.min(box.top + STACK_STEP * index, view.bottom - STACK_STEP));
      stacks.push({ holder, index, x, y });
    }
  }
  return { stacks, centres };
}
